import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { KpiCard } from "@/components/Cards";
import { Store, Inbox, ShieldCheck, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";

export default function BrokerDashboard() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/broker/dashboard").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="p-10 text-navy-500">Loading…</div>;
    const k = data.kpis;

    const statusColor = {
        Submitted: "bg-gold-500/10 text-gold-700 border-gold-200",
        InProgress: "bg-blue-50 text-blue-700 border-blue-200",
        InForce: "bg-emerald-50 text-emerald-700 border-emerald-200",
        Closed: "bg-emerald-50 text-emerald-700 border-emerald-200",
        Expired: "bg-navy-50 text-navy-500 border-navy-200",
        Declined: "bg-red-50 text-red-700 border-red-200",
    };

    return (
        <>
            <PageHeader
                eyebrow="Vendor console"
                title="Vendor dashboard"
                description="Live performance across every product you list on Perx."
                testid="broker-dashboard-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-8">
                <div className="grid sm:grid-cols-2 gap-5">
                    <KpiCard label="Live products" value={k.live_products} icon={Store} />
                    <KpiCard
                        label="Pending applications"
                        value={k.pending_applications}
                        icon={Inbox}
                        accent="pink"
                    />
                </div>

                {/* Approval tile */}
                <ApprovalTile
                    count={k.awaiting_approval || 0}
                    products={data.awaiting_approval || []}
                />

                <div className="grid lg:grid-cols-2 gap-8">
                    <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft p-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-4">
                            Active products
                        </div>
                        <div className="space-y-3">
                            {(data.active_products || []).length === 0 && (
                                <div className="text-sm text-navy-500 py-4 text-center">
                                    No active products linked to your vendor yet.
                                </div>
                            )}
                            {(data.active_products || []).map((d) => {
                                const symbol = d.is_paid_USD ? "US$" : "$";
                                return (
                                    <div
                                        key={d.id}
                                        data-testid={`active-product-${d.id}`}
                                        className="flex items-center justify-between px-4 py-3 rounded-lg bg-cream border border-navy-100/40"
                                    >
                                        <div className="text-navy-700 font-medium">
                                            {d.title}
                                        </div>
                                        <div className="font-display text-lg text-navy-700">
                                            {symbol}
                                            {d.price?.toFixed(2)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft p-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-4">
                            Latest applications
                        </div>
                        <div className="space-y-3">
                            {data.recent_applications.map((a) => (
                                <Link
                                    key={a.id}
                                    to={`/broker/applications/${a.id}`}
                                    data-testid={`recent-app-${a.id}`}
                                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-cream border border-navy-100/40 hover:border-gold-500 transition-colors"
                                >
                                    <div>
                                        <div className="text-navy-700 font-medium">
                                            {a.member?.name || "Household member"}
                                        </div>
                                        <div className="text-xs text-navy-500 mt-0.5">
                                            {a.product_name} ·{" "}
                                            {a.created_at
                                                ? new Date(a.created_at).toLocaleDateString()
                                                : ""}
                                        </div>
                                    </div>
                                    <Badge
                                        className={`${statusColor[a.application_status] || ""} border`}
                                    >
                                        {a.application_status}
                                    </Badge>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

/** Dashboard tile linking to the products waiting for this vendor's approval. */
function ApprovalTile({ count, products }) {
    const hasPending = count > 0;
    return (
        <Link
            to="/broker/products"
            data-testid="vendor-dashboard-approval-tile"
            className="group block rounded-2xl overflow-hidden border shadow-soft transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-lift"
            style={{
                background: hasPending
                    ? "linear-gradient(135deg, #001c50 0%, #003888 100%)"
                    : "linear-gradient(135deg, #f7f4ed 0%, #ffffff 100%)",
                borderColor: hasPending ? "#001c50" : "rgba(0, 28, 80, 0.1)",
            }}
        >
            <div className="p-6 lg:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-6">
                <div
                    className={`h-14 w-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                        hasPending ? "bg-gold-500/20" : "bg-navy-50"
                    }`}
                >
                    <ShieldCheck
                        size={26}
                        className={hasPending ? "text-gold-500" : "text-navy-700"}
                        strokeWidth={1.6}
                    />
                </div>
                <div className="flex-1 min-w-0">
                    <div
                        className={`text-[10px] uppercase tracking-[0.3em] mb-1.5 ${
                            hasPending ? "text-gold-500" : "text-gold-600"
                        }`}
                    >
                        Product approval
                    </div>
                    <div
                        className={`font-display text-2xl mb-1 ${
                            hasPending ? "text-white" : "text-navy-700"
                        }`}
                    >
                        {hasPending
                            ? `${count} product${count === 1 ? "" : "s"} awaiting your approval`
                            : "Nothing awaiting approval"}
                    </div>
                    <div
                        className={`text-sm ${
                            hasPending ? "text-white/70" : "text-navy-500"
                        }`}
                    >
                        {hasPending
                            ? "Members won't see these until you approve them."
                            : "Nothing to review — you're up to date."}
                    </div>
                    {hasPending && products.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                            {products.slice(0, 3).map((p) => (
                                <span
                                    key={p.id}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-white text-xs px-3 py-1 border border-white/20"
                                >
                                    {p.title}
                                </span>
                            ))}
                            {products.length > 3 && (
                                <span className="inline-flex items-center rounded-full bg-white/10 text-white/70 text-xs px-3 py-1">
                                    +{products.length - 3} more
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div
                    className={`flex-shrink-0 inline-flex items-center gap-2 text-sm font-semibold rounded-full h-11 px-5 border transition-colors ${
                        hasPending
                            ? "bg-gold-500 text-navy-900 border-gold-500 group-hover:bg-gold-600"
                            : "bg-white text-navy-700 border-navy-100 group-hover:border-gold-500"
                    }`}
                >
                    {hasPending ? "Review & approve" : "View products"}
                    <ArrowRight size={16} />
                </div>
            </div>
        </Link>
    );
}
